// Child selector for the parent panel — loads linked children, remembers the
// last selection in localStorage, and reports the active child to the parent page.
import React, { useEffect, useState } from 'react';
import { Users, Loader2, AlertCircle } from 'lucide-react';
import { fetchMyChildren, ParentChild } from '../services/parentService';

const STORAGE_KEY = 'nexedu_parent_selected_child';

interface ChildSelectorProps {
  onChildChange: (studentId: string | null) => void;
}

export const ChildSelector: React.FC<ChildSelectorProps> = ({ onChildChange }) => {
  const [children, setChildren] = useState<ParentChild[]>([]);
  const [selectedId, setSelectedId] = useState<string>('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    const load = async () => {
      try {
        setLoading(true);
        const data = await fetchMyChildren();
        if (cancelled) return;
        setChildren(data);

        const stored = localStorage.getItem(STORAGE_KEY);
        const initial = data.find((c) => c.studentId === stored)?.studentId || data[0]?.studentId || '';
        setSelectedId(initial);
        onChildChange(initial || null);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Erreur lors du chargement des enfants');
          onChildChange(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
    // onChildChange is intentionally omitted: parent pages pass inline callbacks
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (studentId: string) => {
    setSelectedId(studentId);
    localStorage.setItem(STORAGE_KEY, studentId);
    onChildChange(studentId);
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-500 mb-4">
        <Loader2 className="animate-spin" size={16} />
        Chargement de vos enfants...
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm mb-4">
        <AlertCircle size={16} />
        {error}
      </div>
    );
  }

  if (children.length === 0) {
    return (
      <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg text-amber-700 text-sm mb-4">
        <AlertCircle size={16} />
        Aucun enfant n'est lié à votre compte. Contactez l'administration de l'établissement.
      </div>
    );
  }

  // Single child: show a label, no dropdown needed
  if (children.length === 1) {
    return (
      <div className="flex items-center gap-2 text-sm text-gray-700 mb-4">
        <Users size={16} className="text-primary-600" />
        <span className="font-medium">
          {children[0].firstName} {children[0].lastName}
        </span>
        <span className="text-gray-400">({children[0].studentCode})</span>
      </div>
    );
  }

  return (
    <div className="flex items-center gap-3 mb-4">
      <Users size={16} className="text-primary-600" />
      <label className="text-sm text-gray-600">Enfant :</label>
      <select
        value={selectedId}
        onChange={(e) => handleChange(e.target.value)}
        className="px-3 py-1.5 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-primary-500 focus:border-primary-500 bg-white"
      >
        {children.map((child) => (
          <option key={child.studentId} value={child.studentId}>
            {child.firstName} {child.lastName} ({child.studentCode})
          </option>
        ))}
      </select>
    </div>
  );
};
